import { z } from 'zod';

export const tasksSearchSchema = z.object({
  status: z.string().optional(),
  priority: z.string().optional(),
  show: z.string().optional(),
  starred: z.string().optional(),
  labels: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().optional(),
  /**
   * User/team/agent id to filter tasks by assignee. Used by both the
   * dashboard "tasks by assignee" drilldown and the explicit Assignee
   * chip on the All tab.
   */
  assignedTo: z.string().uuid().optional(),
  /**
   * Disambiguator for `assignedTo` — pins the polymorphic
   * `assigned_to_type` on the API filter so a UUID can't accidentally
   * match across user/team/agent namespaces. Validated at the URL
   * boundary so a malformed `?assigneeType=banana` is rejected by the
   * router rather than silently flowing into the API call.
   */
  assigneeType: z.enum(['user', 'team', 'agent']).optional(),
  // Drilldown bookkeeping — present iff the user navigated here from
  // a dashboard tile. The drilldown banner reads these to render the
  // "← Back to dashboard" link; the page reads `from=dashboard` to
  // suppress writes to the saved tasks-inbox preference.
  from: z.string().optional(),
  dashFrom: z.string().optional(),
  dashTo: z.string().optional(),
  dashPreset: z.string().optional(),
  /**
   * Human-readable label of WHAT was drilled into. Banner renders
   * "Filtered by <dashLabel>" so the user has visible context for the
   * narrowing. Pure display — server doesn't read it. Capped at 80
   * chars so a maliciously-crafted URL can't bloat the address bar.
   */
  dashLabel: z.string().max(80).optional(),
  /**
   * Interaction whose Call detail sheet is open. Written by
   * `useCallDetailParam`, which is shared with `/referrals` — the sheet is
   * opened from `TaskWorkPanel`, which both routes host. Declared here or the
   * router strips it on write.
   */
  call: z.string().uuid().optional(),
  // Legacy fields (kept for URL backwards compatibility)
  readStatus: z.enum(['read', 'unread', 'all']).optional(),
});

/**
 * Inferred type — exported so dashboard drilldown URL builders can
 * `satisfies TasksSearch` their output instead of using the loose
 * `Record<string, string | undefined>` shape. Catches "drilldown
 * sends a key the destination doesn't recognize" at compile time.
 */
export type TasksSearch = z.infer<typeof tasksSearchSchema>;
